import { useState } from "react";
import { ApiError, api, type Asesor } from "@/lib/api";
import Modal from "@/ui/Modal";
import { useToast } from "@/ui/Toast";
import ListaAsesores from "./ListaAsesores";
import { useAsesores } from "./useAsesores";

type Props = {
  clienteId: number;
  clienteNombre: string;
  /** Asesor asignado hoy a esa empresa (null = ninguno). */
  asesorActualId: number | null;
  onClose: () => void;
  /** Se llama tras guardar, para que el caso recargue lo que hereda. */
  onSaved: () => void;
};

/**
 * Cambia el asesor comercial **de la empresa** sin salir del caso.
 *
 * Antes, el ✎ del caso navegaba a Configuración → Contactos y dejaba al
 * usuario tirado ahí (no había regreso). El asesor sigue viviendo en el
 * cliente —el caso solo lo hereda—, así que el modal lo dice claro: el
 * cambio aplica a TODOS los casos de esa empresa, no nada más a este.
 */
export default function AsignarAsesorModal({
  clienteId,
  clienteNombre,
  asesorActualId,
  onClose,
  onSaved,
}: Props) {
  const toast = useToast();
  const { asesores, cargando } = useAsesores();
  const [guardando, setGuardando] = useState(false);

  async function guardar(asesorId: number | null, nombre?: string) {
    if (guardando) return;
    setGuardando(true);
    try {
      await api.patch(`/api/clientes/${clienteId}`, {
        asesor_comercial_id: asesorId,
      });
      toast.success(
        asesorId ? "Asesor asignado" : "Asesor quitado",
        asesorId ? `${nombre} · ${clienteNombre}` : clienteNombre,
      );
      onSaved();
      onClose();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude cambiar el asesor", msg);
      setGuardando(false);
    }
  }

  return (
    <Modal
      open
      onOpenChange={(o) => {
        if (!o && !guardando) onClose();
      }}
      title="Asesor comercial"
      description={`Empresa: ${clienteNombre}`}
      size="sm"
      footer={
        <button type="button" className="btn-ghost" onClick={onClose} disabled={guardando}>
          Cerrar
        </button>
      }
    >
      <div className="space-y-3">
        <p className="text-xs text-amber-300/90 bg-amber-500/10 border border-amber-500/30 rounded-md px-3 py-2">
          El asesor pertenece a la <strong>empresa</strong>, no al caso: lo que
          elijas aquí aplica a <strong>todos los casos de {clienteNombre}</strong>.
        </p>

        <ListaAsesores
          asesores={asesores}
          cargando={cargando}
          ocupado={guardando}
          value={asesorActualId}
          autoFocus
          altoLista="max-h-64"
          onSelect={(a: Asesor) => void guardar(a.id, a.nombre)}
          onClear={asesorActualId ? () => void guardar(null) : undefined}
          onEscape={() => {
            if (!guardando) onClose();
          }}
        />

        <p className="text-[11px] text-zinc-500">
          Los asesores se dan de alta en Configuración → Asesores comerciales.
        </p>
      </div>
    </Modal>
  );
}
